/**
 * Penyaring barge-in mode suara: ucapan user hanya boleh memotong giliran yang
 * sedang berjalan bila BERMAKNA. Filter gema (eksperimen, sakelar admin #62,
 * bawaan mati) tambahan: ucapan mikrofon yang mirip kalimat Jenna yang SEDANG
 * terdengar diabaikan — tidak pernah setelah Jenna selesai bicara.
 */

/** Minimal kata isi (di luar kata ragu) agar ucapan dianggap bermakna. */
export const BARGE_IN_MIN_WORDS = 2;
/** Atau: durasi bicara melebihi ini dengan minimal satu kata isi. */
export const BARGE_IN_MIN_SPEECH_MS = 600;
/** Jeda sangat singkat setelah frame audio Jenna terakhir (celah antar-frame) yang masih dianggap "sedang terdengar". */
export const JENNA_AUDIO_TAIL_MS = 300;
/** Jeda setelah bunyi tunggu (napas) selesai yang masih rawan tertangkap mikrofon. */
export const WAIT_SOUND_ECHO_TAIL_MS = 1200;
/** Porsi kata transkrip yang cocok dengan ucapan Jenna agar dianggap gema. */
export const ECHO_MIN_OVERLAP = 0.8;
/** Pemisah kalimat: spasi sesudah tanda akhir kalimat. */
const SENTENCE_BOUNDARY = /(?<=[.!?…])\s+/u;
/** Hening lebih lama dari ini = ucapan baru; sisa ucapan lama yang tak didelegasikan dibuang. */
export const UTTERANCE_GAP_MS = 3000;

/** Bunyi ragu — saling cocok satu sama lain saat menilai gema ("Emm" ↔ "Hmm"). */
export const HESITATION_SOUNDS: readonly string[] = [
	"emm",
	"em",
	"hmm",
	"hm",
	"eh",
	"ehm",
	"oh",
	"ah",
	"uh",
	"um",
	"hh",
	"huh",
];
/** Jawaban pendek — tidak memotong giliran, tetapi cocok gema hanya bila sama persis. */
export const SHORT_REPLIES: readonly string[] = ["oke", "ok", "ya", "iya"];

/** Tahap giliran: idle, Claude masih menjawab, atau jawaban lengkap & audio sedang selesai. */
export type TurnPhase = "idle" | "streaming" | "settling";
export type UtteranceVerdict = "accept" | "ignore";

/** Huruf berulang diringkas ("emmm" → "em") hanya untuk mencocokkan kata ragu. */
const squeeze = (word: string) => word.replace(/(.)\1+/g, "$1");
const HESITATION_SET = new Set(HESITATION_SOUNDS.map(squeeze));
const FILLER_SET = new Set([...HESITATION_SET, ...SHORT_REPLIES.map(squeeze)]);

/** Kata huruf kecil tanpa tanda baca. */
export function normalizeWords(text: string): string[] {
	return text
		.toLowerCase()
		.replace(/[^\p{L}\p{N}\s]/gu, " ")
		.split(/\s+/)
		.filter(Boolean);
}

const isHesitation = (word: string) => HESITATION_SET.has(squeeze(word));
const isFillerWord = (word: string) => FILLER_SET.has(squeeze(word));

const contentWordCount = (text: string) =>
	normalizeWords(text).filter((w) => !isFillerWord(w)).length;

/** Ucapan bermakna: ≥ N kata isi, atau bicara > M ms dengan minimal satu kata isi. */
export function isMeaningful(text: string, speechMs: number): boolean {
	const content = contentWordCount(text);
	if (content >= BARGE_IN_MIN_WORDS) return true;
	return content >= 1 && speechMs > BARGE_IN_MIN_SPEECH_MS;
}

/** Transkrip user yang sebagian besar katanya baru saja diucapkan Jenna (atau daftar pengisi admin). */
export function isEcho(
	text: string,
	jennaText: string,
	fillerPhrases: readonly string[],
): boolean {
	const words = normalizeWords(text);
	if (words.length === 0) return false;
	const vocab = new Set(
		normalizeWords([jennaText, ...fillerPhrases].join(" ")),
	);
	const jennaHesitates = [...vocab].some(isHesitation);
	const matched = words.filter(
		(w) => vocab.has(w) || (jennaHesitates && isHesitation(w)),
	).length;
	return matched / words.length >= ECHO_MIN_OVERLAP;
}

export interface UtteranceInput {
	text: string;
	speechMs: number;
	phase: TurnPhase;
	/** Filter gema nyala (sakelar admin #62). Mati = gema tidak pernah dinilai. */
	echoFilter: boolean;
	/** Audio Jenna sedang terdengar saat potongan ucapan terakhir tiba. */
	duringJennaAudio: boolean;
	/** Ucapan terjadi saat/sesaat setelah bunyi tunggu (napas) — butuh kata isi nyata. */
	duringWaitSound?: boolean;
	/** Kalimat Jenna yang sedang diucapkan (bukan riwayat). */
	jennaText: string;
	fillerPhrases: readonly string[];
}

export type IgnoreReason =
	| "empty"
	| "wait_sound"
	| "echo"
	| "hesitation"
	| "none";

/** Alasan keputusan (untuk log tahap — tanpa teks); "none" = diterima. */
export function explainUtterance(u: UtteranceInput): IgnoreReason {
	if (!u.text.trim()) return u.phase === "idle" ? "none" : "empty";
	if (u.duringWaitSound && contentWordCount(u.text) < BARGE_IN_MIN_WORDS)
		return "wait_sound";
	if (
		u.echoFilter &&
		u.duringJennaAudio &&
		isEcho(u.text, u.jennaText, u.fillerPhrases)
	)
		return "echo";
	if (u.phase === "streaming" && !isMeaningful(u.text, u.speechMs))
		return "hesitation";
	return "none";
}

/** Terima ucapan sebagai giliran user, atau abaikan (gema / ragu saat Claude masih menjawab). */
export function judgeUtterance(u: UtteranceInput): UtteranceVerdict {
	return explainUtterance(u) === "none" ? "accept" : "ignore";
}

export interface BargeInGate {
	/** Potongan transkrip ucapan user (`start_ms`/`end_ms` dari GPT-Live bila ada). */
	input(delta: string, now: number, startMs?: number, endMs?: number): void;
	/** Teks yang diucapkan / dikirim untuk diucapkan Jenna. */
	spoke(text: string): void;
	/** Audio Jenna terdengar di speaker. */
	audible(now: number): void;
	/** Bunyi tunggu (napas) mulai/berhenti di speaker; ekornya ikut dianggap rawan gema. */
	waitSound(active: boolean, now: number): void;
	setFillerPhrases(phrases: readonly string[]): void;
	/** Nyalakan/matikan filter gema (sakelar admin #62; bawaan mati). */
	setEchoFilter(enabled: boolean): void;
	text(): string;
	verdict(phase: TurnPhase): UtteranceVerdict;
	/** Ambil ucapan yang terkumpul lalu kosongkan. */
	take(phase: TurnPhase): {
		text: string;
		verdict: UtteranceVerdict;
		reason: IgnoreReason;
	};
}

/** Kalimat terakhir dari teks yang diucapkan Jenna ("A. B c" → "B c"). */
export function currentSentence(text: string): string {
	return text.split(SENTENCE_BOUNDARY).at(-1) ?? "";
}

const num = (v: number | undefined) =>
	typeof v === "number" && Number.isFinite(v) ? v : null;

/** Penampung ucapan user yang sedang berjalan beserta konteks gema Jenna. */
export function createBargeInGate(): BargeInGate {
	let utterance = "";
	let firstStart: number | null = null;
	let lastEnd: number | null = null;
	let firstAt: number | null = null;
	let lastAt: number | null = null;
	let duringAudio = false;
	let duringWait = false;
	let waitActive = false;
	let lastWaitEnd = Number.NEGATIVE_INFINITY;
	let lastAudible = Number.NEGATIVE_INFINITY;
	let spoken = "";
	let echoFilter = false;
	let fillerPhrases: readonly string[] = [];

	const reset = () => {
		utterance = "";
		firstStart = lastEnd = firstAt = lastAt = null;
		duringAudio = false;
		duringWait = false;
	};
	const speechMs = () => {
		if (firstStart !== null && lastEnd !== null) return lastEnd - firstStart;
		return firstAt !== null && lastAt !== null ? lastAt - firstAt : 0;
	};
	const input = (phase: TurnPhase): UtteranceInput => ({
		text: utterance,
		speechMs: speechMs(),
		phase,
		echoFilter,
		duringJennaAudio: duringAudio,
		duringWaitSound: duringWait,
		jennaText: currentSentence(spoken),
		fillerPhrases,
	});
	const verdict = (phase: TurnPhase) => judgeUtterance(input(phase));

	return {
		input(delta, now, startMs, endMs) {
			if (lastAt !== null && now - lastAt > UTTERANCE_GAP_MS) reset();
			utterance += delta;
			firstAt ??= now;
			lastAt = now;
			firstStart ??= num(startMs);
			lastEnd = num(endMs) ?? lastEnd;
			duringAudio = now - lastAudible <= JENNA_AUDIO_TAIL_MS;
			if (waitActive || now - lastWaitEnd <= WAIT_SOUND_ECHO_TAIL_MS)
				duringWait = true;
		},
		spoke(text) {
			// Hanya dua kalimat terakhir disimpan; yang dibandingkan hanya yang terakhir.
			spoken = spoken.concat(text).split(SENTENCE_BOUNDARY).slice(-2).join(" ");
		},
		audible(now) {
			lastAudible = now;
		},
		waitSound(active, now) {
			waitActive = active;
			if (!active) lastWaitEnd = now;
		},
		setFillerPhrases(phrases) {
			fillerPhrases = phrases;
		},
		setEchoFilter(enabled) {
			echoFilter = enabled;
		},
		text: () => utterance.trim(),
		verdict,
		take(phase) {
			const explained = explainUtterance(input(phase));
			const result = {
				text: utterance.trim(),
				verdict:
					explained === "none" ? ("accept" as const) : ("ignore" as const),
				reason: explained,
			};
			reset();
			return result;
		},
	};
}
